import { showErrToast } from "./components/Toasts";
import type Device from "./models/Device";
import { ValueLevels } from "./models/KnxInstanceData";
import { itx } from "./translationItx/itx";

export type ScriptCallParameters = unknown[];

export interface ScriptEngineOptions {
  /**
   * If true, the engine rebuilds itself before every call.
   * Useful when foreign code leaves state in implicit globals.
   */
  isolatePerCall?: boolean;

  /**
   * If true, function detection also looks for let/var in addition to const.
   * Default: true
   */
  supportLetVarAssignments?: boolean;
}

export interface ScriptEngineFunctionInfo {
  name: string;
  kind: "function" | "arrow" | "functionExpression"; // no itx
}

export interface ScriptSandboxContext {}

type FunctionRegistry = Record<string, Function>;

interface InternalState {
  store: Record<string | symbol, unknown>;
  registry: FunctionRegistry;
}

export class GlobalScriptOnline {
  connect() {}

  writeProperty() {
    // (0, 250, 1, 1, 3, v, 0);
    showErrToast(itx("KnxGlobalScriptEngine.onlineModeNotSupported", "Online mode not yet supported"));
  }

  disconnect() {}
}
export class GlobalScriptParameterWrapper {
  constructor(
    public readonly device: Device,
    public readonly id: string,
    public readonly instanceId: string,
    public readonly valueLevel: ValueLevels,
  ) {}

  get value(): string {
    return this.device.knxInstanceData.getValue(this.id, this.instanceId) ?? "";
  }
  set value(newValue: string) {
    this.device.knxInstanceData.setValue(this.valueLevel, newValue, this.id, this.instanceId, "");
  }

  get Value(): string {
    return this.device.knxInstanceData.getValue(this.id, this.instanceId) ?? "";
  }
  set Value(newValue: string) {
    this.device.knxInstanceData.setValue(this.valueLevel, newValue, this.id, this.instanceId, "");
  }
}

export class GlobalScriptDeviceWrapper {
  constructor(
    public readonly device: Device,
    public readonly instanceId: string,
    public readonly valueLevel: ValueLevels,
  ) {}

  getParameterById(id: string) {
    return new GlobalScriptParameterWrapper(this.device, id, this.instanceId, this.valueLevel);
  }
  GetParameterById(id: string) {
    return this.getParameterById(id);
  }

  getParameterByName(name: string) {
    const allPropertyIds = this.device.knxInstanceData.allPropertyIds(this.instanceId);
    for (const id of allPropertyIds) {
      const ext = this.device.knxInstanceData.getExtendedData(id, this.instanceId);
      if (ext?.info.name === name) {
        return new GlobalScriptParameterWrapper(this.device, id, this.instanceId, this.valueLevel);
      }
    }

    console.error(
      `Parameter with name "${name}" not found on device "${this.device.tag}" (instanceId: "${this.instanceId}")`,
    );

    return undefined;
  }
  GetParameterByName(name: string) {
    return this.getParameterByName(name);
  }
}

export class KnxGlobalScriptEngine {
  private readonly source: string;
  private readonly options: Required<ScriptEngineOptions>;
  private readonly functionInfos: ScriptEngineFunctionInfo[];
  private state: InternalState;

  public constructor(source: string, options: ScriptEngineOptions = {}) {
    this.source = source;
    this.options = {
      isolatePerCall: options.isolatePerCall ?? false,
      supportLetVarAssignments: options.supportLetVarAssignments ?? true,
    };

    this.functionInfos = this.extractFunctionInfos(this.source);
    this.state = this.buildState();
  }

  /**
   * Returns all discovered function names.
   */
  public getFunctionNames(): string[] {
    return Object.keys(this.state.registry).filter((name) => typeof this.state.registry[name] === "function");
  }

  /**
   * Returns richer info about discovered functions.
   */
  public getFunctionInfos(): ScriptEngineFunctionInfo[] {
    return [...this.functionInfos];
  }

  /**
   * Checks whether a function exists.
   */
  public hasFunction(funcName: string): boolean {
    return typeof this.state.registry[funcName] === "function";
  }

  /**
   * Calls a discovered function by name.
   */
  public call<T = unknown>(funcName: string, parameters: ScriptCallParameters = [], context: ScriptSandboxContext): T {
    if (!Array.isArray(parameters)) {
      throw new Error("parameters must be an array");
    }

    const currentState = this.options.isolatePerCall ? this.buildState() : this.state;
    this.applySandboxContext(currentState, context);
    const fn = currentState.registry[funcName];

    if (typeof fn !== "function") {
      const available = Object.keys(currentState.registry).sort().join(", ");
      throw new Error(`Function "${funcName}" not found. Available functions: ${available || "(none)"}`);
    }

    let res: any = "";
    try {
      res = fn(...parameters) as T;
    } catch (e) {
      console.error(`Error while executing function "${funcName}": ${(e as Error).message}`);
      console.error(this.source);
    }
    return res;
  }

  /**
   * Rebuilds the current engine state from the original source.
   * Useful if previous calls changed implicit globals.
   */
  public reset(): void {
    this.state = this.buildState();
  }

  /**
   * Alias for reset(), for readability in some call sites.
   */
  public reload(): void {
    this.reset();
  }

  /**
   * Returns a shallow copy of the sandbox content.
   * This includes implicit globals created by the foreign code.
   */
  public getSandboxSnapshot(): Record<string, unknown> {
    const snapshot: Record<string, unknown> = {};

    for (const key of Reflect.ownKeys(this.state.store)) {
      if (typeof key === "string") {
        snapshot[key] = this.state.store[key];
      }
    }

    return snapshot;
  }

  /**
   * Returns the original source.
   */
  public getSource(): string {
    return this.source;
  }

  private applySandboxContext(state: InternalState, context: ScriptSandboxContext): void {
    // if (context.device) {
    //   state.store.device = new GlobalScriptDeviceWrapper(context.device, context.instanceId ?? "");
    //   return;
    // }
    // delete state.store.device;
  }

  private buildState(): InternalState {
    const store: Record<string | symbol, unknown> = Object.create(null);

    const sandbox = new Proxy(store, {
      has: () => true,

      get: (target, key: string | symbol): unknown => {
        if (key === Symbol.unscopables) {
          return undefined;
        }

        if (key in target) {
          return target[key];
        }

        return (globalThis as Record<string | symbol, unknown>)[key];
      },

      set: (target, key: string | symbol, value: unknown): boolean => {
        target[key] = value;
        return true;
      },

      getOwnPropertyDescriptor: (target, key: string | symbol): PropertyDescriptor | undefined => {
        const own = Object.getOwnPropertyDescriptor(target, key);
        if (own) {
          return own;
        }

        return {
          configurable: true,
          enumerable: true,
          writable: true,
          value: undefined,
        };
      },
    });

    const registrySourceEntries = this.functionInfos
      .map((info) => {
        const safeName = JSON.stringify(info.name);
        return `${safeName}: (typeof ${info.name} === "function" ? ${info.name} : undefined)`;
      })
      .join(",\n");

    const factory = new Function(
      "sandbox", // no itx
      `
with (sandbox) {
${this.source}

return {
${registrySourceEntries}
};
}
      `,
    ) as (sandbox: object) => FunctionRegistry;

    const registry = factory(sandbox);

    return { store, registry };
  }

  private extractFunctionInfos(source: string): ScriptEngineFunctionInfo[] {
    const found = new Map<string, ScriptEngineFunctionInfo>();

    const assignmentKeyword = this.options.supportLetVarAssignments ? "(?:const|let|var)" : "const";

    const patterns: Array<{
      kind: ScriptEngineFunctionInfo["kind"]; // no itx
      regex: RegExp;
    }> = [
      {
        kind: "function", // no itx
        regex: /\bfunction\s+([A-Za-z_$][A-Za-z0-9_$]*)\s*\(/g,
      },
      {
        kind: "arrow", // no itx
        regex: new RegExp(
          String.raw`\b${assignmentKeyword}\s+([A-Za-z_$][A-Za-z0-9_$]*)\s*=\s*(?:async\s*)?(?:\([^)]*\)|[A-Za-z_$][A-Za-z0-9_$]*)\s*=>`,
          "g",
        ),
      },
      {
        kind: "functionExpression", // no itx
        regex: new RegExp(
          String.raw`\b${assignmentKeyword}\s+([A-Za-z_$][A-Za-z0-9_$]*)\s*=\s*(?:async\s*)?function\b`,
          "g",
        ),
      },
    ];

    for (const pattern of patterns) {
      for (const match of source.matchAll(pattern.regex)) {
        const name = match[1];
        if (!name) {
          continue;
        }

        if (!found.has(name)) {
          found.set(name, {
            name,
            kind: pattern.kind,
          });
        }
      }
    }

    return [...found.values()];
  }
}
